import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { closePosDay, posError } from "../../../../services/PosApiServices";
import { num } from "../posHelpers";
import { printCloseSlip, useReceiptHeader } from "./posPrint";
import { amt, day, time, CashDiff, Empty, PageHead, Stat, TotalsStats } from "./PosParts";

const TOTAL_KEYS = [
  "billCount",
  "returnCount",
  "salesTotal",
  "returnTotal",
  "netTotal",
  "cash",
  "card",
  "upi",
  "credit",
  "expectedCash",
  "countedCash",
  "cashDifference",
];

/**
 * POS ▸ Day Close. Closes the branch's business date once every counter's
 * shift is closed; POS bills then go on the next date. A date with no sales is
 * closed the same way, so the dates always follow one another.
 */
const PosDayClose = ({ branch, userId, state, reload, permit, onShiftClose }) => {
  const header = useReceiptHeader();
  const [remarks, setRemarks] = useState("");
  const [printSlip, setPrintSlip] = useState(true);
  const [busy, setBusy] = useState(false);

  const date = state?.transactionDate ? dayjs(state.transactionDate) : null;
  const ahead = Boolean(date && date.isAfter(dayjs(), "day"));
  const behind = Boolean(date && date.isBefore(dayjs(), "day"));
  const openShifts = useMemo(
    () => (state?.counters || []).filter((c) => c.billCount + c.returnCount > 0),
    [state]
  );
  const closed = useMemo(() => state?.closedShifts || [], [state]);
  const dayTotals = useMemo(
    () =>
      closed.reduce(
        (t, s) => Object.fromEntries(TOTAL_KEYS.map((k) => [k, num(t[k]) + num(s[k])])),
        {}
      ),
    [closed]
  );

  const blocked = !date || ahead || openShifts.length > 0;

  const closeDay = async () => {
    if (blocked) return;
    const next = date.add(1, "day").format("DD MMM YYYY");
    const answer = await Swal.fire({
      title: `Close ${date.format("DD MMM YYYY")}?`,
      html: closed.length
        ? `${closed.length} closed shift(s), net <b>${amt(dayTotals.netTotal)}</b>.<br/>POS bills will then go on <b>${next}</b>.`
        : `There were no POS sales on this date.<br/>POS bills will then go on <b>${next}</b>.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4B44D6",
      cancelButtonColor: "#67728A",
      confirmButtonText: "Close the day",
    });
    if (!answer.isConfirmed) return;

    setBusy(true);
    try {
      const result = await closePosDay({
        branchID: branch.guid,
        remarks: remarks.trim() || null,
        userID: userId?.id || null,
      });
      toast.success(`Day closed. The business date is now ${dayjs(result.nextDate).format("DD MMM YYYY")}.`);
      if (printSlip) printCloseSlip("day", result, header);
      setRemarks("");
    } catch (error) {
      toast.error(posError(error, "Could not close the day"));
    } finally {
      setBusy(false);
      reload();
    }
  };

  return (
    <>
      <PageHead title="Day Close" subtitle={`Business date ${day(state?.transactionDate)}`}>
        <button type="button" className="btn btn-light" onClick={reload}>
          Refresh
        </button>
      </PageHead>

      {ahead ? (
        <div className="pm-note pm-note--warn">
          The business date is after today, so it cannot be closed yet.
        </div>
      ) : null}
      {behind ? (
        <div className="pm-note pm-note--warn">
          The business date is behind today: close it (and any later days without sales) to bring the till up to date.
        </div>
      ) : null}

      <div className="pm-cols">
        <div>
          <div className="pm-card">
            <h5>Open shifts</h5>
            {openShifts.length ? (
              <>
                <div className="pm-note pm-note--bad">
                  Close these shifts before closing the day.
                </div>
                <div className="pm-table-wrap">
                  <table className="pm-table">
                    <thead>
                      <tr>
                        <th>Counter</th>
                        <th>Shift</th>
                        <th className="num">Bills</th>
                        <th className="num">Returns</th>
                        <th className="num">Net</th>
                        <th>Last bill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openShifts.map((c) => (
                        <tr key={c.counterID}>
                          <td>{c.counterName}</td>
                          <td>{c.shiftNo}</td>
                          <td className="num">{c.billCount}</td>
                          <td className="num">{c.returnCount}</td>
                          <td className="num">{amt(c.netTotal)}</td>
                          <td>{time(c.lastBillAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="btn btn-light mt-2" onClick={onShiftClose}>
                  Go to Shift Close
                </button>
              </>
            ) : (
              <Empty>Every shift on this date is closed.</Empty>
            )}
          </div>

          <div className="pm-card">
            <h5>Closed shifts</h5>
            {closed.length ? (
              <div className="pm-table-wrap">
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th>Counter</th>
                      <th>Shift</th>
                      <th className="num">Bills</th>
                      <th className="num">Net</th>
                      <th className="num">Cash</th>
                      <th className="num">Counted</th>
                      <th className="num">Difference</th>
                      <th>Closed by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closed.map((s) => (
                      <tr key={s.guid}>
                        <td>{s.counterName}</td>
                        <td>{s.shiftNo}</td>
                        <td className="num">{s.billCount}</td>
                        <td className="num">{amt(s.netTotal)}</td>
                        <td className="num">{amt(s.cash)}</td>
                        <td className="num">{amt(s.countedCash)}</td>
                        <td className="num">
                          <CashDiff value={s.cashDifference} />
                        </td>
                        <td>{s.closedUserName || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>No shift was closed on this date.</Empty>
            )}
          </div>
        </div>

        <div className="pm-card">
          <h5>Day totals</h5>
          <TotalsStats t={dayTotals} />
          <div className="pm-stats mt-2">
            <Stat label="Expected cash" value={amt(dayTotals.expectedCash)} />
            <Stat label="Counted cash" value={amt(dayTotals.countedCash)} />
          </div>
          <p className="mt-2 mb-3">
            Difference: <CashDiff value={num(dayTotals.cashDifference)} />
          </p>
          <div className="pm-field">
            <label htmlFor="pm-day-remarks">Remarks</label>
            <input
              id="pm-day-remarks"
              className="form-control"
              maxLength={250}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <div className="form-check mb-3">
            <input
              id="pm-day-print"
              type="checkbox"
              className="form-check-input"
              checked={printSlip}
              onChange={(e) => setPrintSlip(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="pm-day-print">
              Print the day slip
            </label>
          </div>
          <button
            type="button"
            className="btn btn-pm w-100"
            onClick={closeDay}
            disabled={busy || blocked || !permit.add}
            title={permit.add ? "" : "You do not have the right to close the day"}
          >
            {busy ? "Closing…" : `Close ${day(state?.transactionDate)}`}
          </button>
        </div>
      </div>
    </>
  );
};

PosDayClose.propTypes = {
  branch: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  state: PropTypes.object,
  reload: PropTypes.func.isRequired,
  permit: PropTypes.object.isRequired,
  onShiftClose: PropTypes.func.isRequired,
};

export default PosDayClose;
